/**
 * FirmNameAutocomplete — a Firmenname text input with register suggestions (Zefix).
 *
 * The one shared combobox: used by the Neuer-Kunde wizard and the Firmenstammdaten edit
 * form. Controlled: the host owns `value`; `onChange` fires on every keystroke, `onPick` when
 * a suggestion is chosen (name + uid + seat) so the host can fill the UID too.
 *
 * Suggestions come from useFirmNameSuggestions (one register fetch per 3-char stem, ranked
 * locally after that). 6 rows are shown, purely visual; a hint says how many more are hidden.
 * Known limit: a typo inside the first 3 characters fetches the wrong list and nothing here
 * can repair it. When the register is unreachable the field is a plain text input.
 *
 * Keyboard: ArrowUp/Down move, Enter picks (and does not submit the form), Escape closes.
 * Options use onMouseDown+preventDefault so the input's blur does not swallow the click.
 * After a pick the list stays closed until the value changes again.
 */

import { useId, useState } from 'react';
import type { FirmNameSuggestion } from '@brokly/shared';
import { cn } from '../../lib/cn';
import { useFirmNameSuggestions } from '../../features/firms/hooks/useFirmNameSuggestions';
import { FIRM_NAME_VISIBLE_ROWS } from '../../features/firms/constants';

export function FirmNameAutocomplete({
  value,
  onChange,
  onPick,
  disabled,
  placeholder,
  id,
  'aria-label': ariaLabel,
}: {
  value: string;
  onChange: (name: string) => void;
  onPick?: (row: FirmNameSuggestion) => void;
  disabled?: boolean;
  placeholder?: string;
  id?: string;
  'aria-label'?: string;
}) {
  const listId = useId();
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const [picked, setPicked] = useState<string | null>(null);
  const { suggestions, isError } = useFirmNameSuggestions(value);

  const visible = suggestions.slice(0, FIRM_NAME_VISIBLE_ROWS);
  const hidden = suggestions.length - visible.length;
  const showList = open && !disabled && !isError && value !== picked && visible.length > 0;
  const activeIdx = showList && active < visible.length ? active : -1;

  function pick(row: FirmNameSuggestion) {
    setPicked(row.name);
    setOpen(false);
    setActive(-1);
    onChange(row.name);
    onPick?.(row);
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setOpen(true);
      setActive((i) => Math.min(i + 1, visible.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      if (showList && activeIdx >= 0) {
        e.preventDefault();
        pick(visible[activeIdx]);
      }
    } else if (e.key === 'Escape') {
      if (showList) e.stopPropagation();
      setOpen(false);
    }
  }

  return (
    <div className="relative">
      <input
        id={id}
        type="text"
        role="combobox"
        aria-label={ariaLabel}
        aria-autocomplete="list"
        aria-expanded={showList}
        aria-controls={listId}
        aria-activedescendant={activeIdx >= 0 ? `${listId}-${activeIdx}` : undefined}
        autoComplete="off"
        value={value}
        disabled={disabled}
        placeholder={placeholder}
        onChange={(e) => {
          onChange(e.target.value);
          setOpen(true);
          setActive(-1);
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        onKeyDown={onKeyDown}
        className="h-9 w-full rounded-md border border-border bg-background px-3 text-sm outline-none focus-visible:ring-2 focus-visible:ring-ring"
      />
      {showList && (
        <div className="absolute left-0 right-0 top-full z-20 mt-1 overflow-hidden rounded-md border border-border bg-card shadow-md">
          <ul id={listId} role="listbox" aria-label="Vorschläge aus dem Handelsregister">
            {visible.map((row, i) => (
              <li
                key={`${row.name}|${row.uid ?? ''}`}
                id={`${listId}-${i}`}
                role="option"
                aria-selected={i === activeIdx}
                onMouseDown={(e) => e.preventDefault()}
                onMouseEnter={() => setActive(i)}
                onClick={() => pick(row)}
                className={cn(
                  'flex cursor-pointer items-baseline justify-between gap-3 px-3 py-1.5 text-sm',
                  i === activeIdx ? 'bg-secondary' : 'hover:bg-secondary/60',
                )}
              >
                <span className="truncate">{row.name}</span>
                {row.legalSeat && (
                  <span className="shrink-0 text-xs text-muted-foreground">{row.legalSeat}</span>
                )}
              </li>
            ))}
          </ul>
          <div className="flex justify-between border-t border-border px-3 py-1 text-[11px] text-muted-foreground">
            <span>{hidden > 0 ? `${hidden} weitere, weiter tippen` : ''}</span>
            <span>Quelle: Zefix</span>
          </div>
        </div>
      )}
    </div>
  );
}
